import {Container, Sprite} from 'pixi.js';
import {CoreDI, IAnimation, inject, ResourceLoader, ScreenParameters, Tween, UpdateManager} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {IReelsScaleService} from '@gongxigames/framework/slot/types';
import {BackgroundConstants} from '@gongxigames/framework/slot/modules/background/BackgroundConstants';
import {IBackgroundView} from '@gongxigames/framework/slot/modules/background/IBackgroundView';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export type Background = {
    name: string;
    img: string;
    spine?: {
        name: string;
        animationName: string;
        animation?: IAnimation;
        skinName?: string;
    };
    scene?: string;

    // automatically created
    container?: Container;
    containerFill?: Container;
    containerImg?: Container;
    containerSpine?: Container;
    containerSafe?: Container;
    spriteImg?: Sprite;
    width?: number;
    height?: number;
};

export class BackgroundViewFullScreen extends View implements IBackgroundView {
    public static readonly NAME: symbol = BackgroundConstants.backgroundViewName;

    @inject(CoreDI.ResourceLoader)
    protected readonly _loader!: ResourceLoader;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    protected readonly _updater: UpdateManager = new UpdateManager(true);
    protected _currentBackground?: Background;

    /**
     * For override
     */
    protected _backgrounds: Background[] = [{
        name: BackgroundConstants.BASE_VIEW,
        img: 'background',
        spine: {name: BackgroundConstants.SPINE_NAME, animationName: 'animation'}
    }, {
        name: BackgroundConstants.FREE_SPINS_VIEW,
        img: 'background_fg',
        spine: {name: BackgroundConstants.SPINE_NAME, animationName: 'animation'}
    }];

    /**
     * For override
     */
    protected _defaultBackgroundName: string = BackgroundConstants.BASE_VIEW;

    public init(): void {
        super.init();

        this.updateManager.add(this._updater);
        this.createElements();
        this.playTransition(this._defaultBackgroundName);
    }

    public playTransition(name: string): void {
        const config = this.getConfig(name);

        if (!this._currentBackground) {
            this._currentBackground = config;
            this.container.addChild(config.container!);
            config.container!.alpha = 1;

            return;
        }

        this._currentBackground = config;
        this.container.addChild(config.container!);

        this._backgrounds.forEach(bg => {
            const isNew = bg === config;

            this._updater.add(new Tween(bg.container)
                .to({alpha: isNew ? 1 : 0}, BackgroundConstants.TRANSFER_TIME)
                .onStart(() => isNew && bg.spine?.animation?.start())
                .onComplete(() => !isNew && bg.spine?.animation?.pause())
                .start()
            );
        });
    }

    public onResourcesLoaded(): void {
        this.createElements();
    }

    public onResized(screenParams: ScreenParameters): void {
        this._backgrounds.forEach(background => {
            const bgWidth = background.width ?? background.spriteImg?.width ?? 1;
            const bgHeight = background.height ?? background.spriteImg?.height ?? 1;
            const result = screenParams.calcScreenFillProps(bgWidth, bgHeight);

            background.containerFill!.scale.set(result.scale);
            background.containerFill!.position.set(result.x, result.y);

            background.spine?.animation?.container.position.set(bgWidth / 2, bgHeight / 2);

            if (background.containerSafe) {
                this.applySafeContainerPlatformScale(background.containerSafe, screenParams);
            }
        });

        this.onCreatedElements();
    }

    /**
     * For override
     */
    protected onCreatedElements(): void {}

    private applySafeContainerPlatformScale(container: Container, {isLandscapeOrientation, isPortraitOrientation}: ScreenParameters): void {
        if (this.isPlatformMobile()) {
            if (isPortraitOrientation) {
                this._reelsScaleService.reelsMobilePortraitScale(container);
            }

            if (isLandscapeOrientation) {
                this._reelsScaleService.reelsMobileLandscapeScale(container);
            }
        }
    }

    private createElements(): void {
        this._backgrounds.forEach(bg => {
            bg.containerSafe?.destroy();
            bg.spriteImg?.destroy();
            delete bg.containerSafe;
            delete bg.spriteImg;

            if (!bg.container) {
                bg.container = new Container();
                bg.containerFill = new Container();
                bg.containerImg = new Container();

                bg.container.addChild(bg.containerFill);
                bg.containerFill.addChild(bg.containerImg);

                bg.container.name = bg.name;
                bg.containerImg.name = 'bg_fill';
                bg.containerImg.name = 'bg_image';

                bg.container.alpha = 0;
            }

            if (this._loader.getTexture(bg.img) && !bg.spriteImg) {
                bg.spriteImg = this._objectBuilder.buildImage({src: bg.img});
                bg.containerImg!.addChild(bg.spriteImg);
            }

            if (!bg.containerSpine && this.isSpineIsExistsAndLoaded(bg)) {
                const spine = bg.spine!;

                bg.containerSpine = new Container();
                bg.containerSpine.name = 'bg_spine';
                bg.containerFill!.addChild(bg.containerSpine);

                spine.animation = this.spineUtils.createSpineAnimation(spine.name, spine.animationName, true, spine.skinName);

                bg.containerSpine!.addChild(spine.animation.container);
                this._updater.add(spine.animation);

                spine.animation.start();
            }

            if (!bg.containerSafe && bg.scene) {
                const scene = this._objectBuilder.buildDisplayObjectByName(bg.scene);

                bg.containerSafe = new Container();
                bg.containerSafe.name = 'bg_scene';
                bg.container.addChild(bg.containerSafe);

                bg.containerSafe.addChild(scene);
            }
        });
    }

    private isSpineIsExistsAndLoaded(bg: Background): boolean {
        return !!bg.spine &&
            !bg.spine.animation &&
            this._loader.isSpineLoaded(bg.spine.name);
    }

    private getConfig(name: string): Background {
        return this._backgrounds.find(bg => bg.name === name)!;
    }
}
